import Autocomplete from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceActivityIcon } from "@/components/DeviceActivityIcon";
import {
  DEVICE_FIELDS_WITH_FIRMWARE,
  useDevicesBySearch,
} from "@/hooks/devices/useDevicesBySearch";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { deviceActivity } from "@/utilities/deviceActivity";

interface Props {
  value: DeviceWithVehicleInfo | null;
  onChange: (device: DeviceWithVehicleInfo | null) => void;
}

export function DeviceSelect({ value, onChange }: Props) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // The firmware fields are asked for because the checklist reads that line straight off the
  // selected vehicle, and a short search applies this list as a property selector.
  const { data, isLoading } = useDevicesBySearch(inputValue, false, DEVICE_FIELDS_WITH_FIRMWARE);
  const devices: DeviceWithVehicleInfo[] = data ?? [];

  // Derived from the query's own data rather than from `devices`, which is a fresh array on every
  // render. Sorted, because this array is part of the status query key: in search order it changes
  // with every keystroke that merely reorders the same vehicles, and every status is fetched again.
  //
  // Empty while the list is closed, which switches the query off. The status is only ever seen
  // inside the open list, and asking for it regardless polls every vehicle the search returned
  // once every fifteen seconds — in a background tab too — for as long as the page stays open.
  const deviceIds = useMemo(
    () => (isOpen ? (data ?? []).map(device => device.id).sort() : []),
    [data, isOpen],
  );
  const { data: statusInfoMap = {} } = useStatusInfosByIds(deviceIds);

  return (
    <Autocomplete
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={devices}
      getOptionLabel={device => device.name ?? ""}
      getOptionKey={device => device.id}
      renderOption={(props, device) => (
        <li {...props} key={device.id}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: "100%" }}>
            <DeviceActivityIcon activity={deviceActivity(statusInfoMap[device.id])} />
            <Typography variant="body2" noWrap>
              {device.name}
              {device.licensePlate ? ` · ${device.licensePlate}` : ""}
            </Typography>
          </Stack>
        </li>
      )}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={isLoading}
      sx={{ minWidth: 300 }}
      renderInput={params => (
        <TextField
          {...params}
          label={t("liveData.selectDevice", "Select device")}
          placeholder={t("liveData.searchDevice", "Search by name, license plate, or VIN")}
          size="small"
        />
      )}
    />
  );
}
